<script setup>
import moment from "moment";
import { ref, computed, watch } from "vue";
import useChatWidget from "../stores/chatWidget";
import { Button, Icon } from "../../components";
import { getPrice, showDurationUnit } from "../../utilities";

const $chatWidget = useChatWidget();
const $config = $chatWidget.getConfig;
const $globalSetting = computed(() => $chatWidget.getGlobalSettings());
const $setting = $chatWidget.getSettings;
const $payload = $chatWidget.getPayload();

const handleConfirm = async () => {
  setTimeout(async () => {
    await $chatWidget.publish();

    const tempPayload = $payload;
    tempPayload.confirm = true;
    $chatWidget.updatePayload(tempPayload);
  }, 1000);

  setTimeout(() => {
    const overviewConfirm = document.getElementById(
      "prebook-chat-component-overview-confirm"
    );

    overviewConfirm.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "nearest",
    });
  }, 1500);
};

const finishBooking = () => {
  const tempConfig = $config;
  tempConfig.isActive = false;
  $chatWidget.updateConfig(tempConfig);

  // TODO: Need to reset the payload to start from zero
  const tempPayload = { step: 0 };
  $chatWidget.updatePayload(tempPayload);
};
</script>

<template>
  <div id="prebook-chat-component-overview" class="px-4 py-[15px]">
    <div class="inline-flex w-[50px]">
      <img
        :src="$globalSetting?.settings?.business?.full_logo_url"
        :alt="$globalSetting?.settings?.business?.name"
        class="w-full object-contain"
      />
    </div>
    <h2 class="text-[20px]">
      {{ $setting.overview.question }}
    </h2>
    <div>
      <div class="flex items-center gap-4 py-3">
        <div class="flex items-center gap-2">
          <div class="text-[#101828] text-sm font-medium">Your Appointment</div>
        </div>
      </div>

      <div class="py-5 border-y border-[#EAECF0]">
        <div class="flex items-center gap-3">
          <div
            class="w-[52px] h-[52px] flex items-center justify-center rounded-[6px] flex-shrink-0"
            :style="{
              'background-color': $payload?.service?.color,
            }"
          >
            <img
              v-if="$payload.service.thumbnail_image_url"
              :src="$payload.service.thumbnail_image_url"
              :alt="$payload.service.name"
              class="w-[52px] h-[52px] object-cover rounded-[6px] border-2 border-white"
            />
            <span v-else class="inline-flex text-[20px] text-white font-medium">
              {{ $payload.service.name.substring(0, 1).toUpperCase() }}
            </span>
          </div>
          <div>
            <div class="text-[#101828] text-lg font-semibold">
              {{ $payload.service.name }}
            </div>
            <div class="text-[#475467] text-sm">
              {{ $payload.staff.name }}
            </div>
          </div>
          <div class="text-[#101828] text-lg font-semibold ml-auto">
            {{ getPrice($payload.service.price) }}
          </div>
        </div>
      </div>

      <div class="mt-5 mb-4 flex flex-col gap-5">
        <div
          class="flex items-center justify-between gap-3 text-[#101828] text-sm"
        >
          <div>Duration</div>
          <div>
            {{
              showDurationUnit(
                $payload.service.duration,
                $payload.service.duration_unit
              ) || "0 min"
            }}
          </div>
        </div>
        <div
          class="flex items-center justify-between gap-3 text-[#101828] text-sm"
        >
          <div>Time</div>
          <div>
            {{ moment($payload.service.time).format("DD MMM YYYY h:mm A") }}
          </div>
        </div>
        <!-- <div
          class="flex items-center justify-between gap-3 text-[#101828] text-sm"
        >
          <div>Location</div>
          <div class="max-w-[50%] text-end">
            6391 Elgin St. Celina, Delaware 10299
          </div>
        </div> -->
      </div>

      <div class="flex gap-5 py-3">
        <Button
          @click="handleConfirm"
          size="sm"
          radius="square"
          class="w-full border-[2px] bg-gray-800 hover:bg-gray-900 border-gray-800 font-medium focus:shadow-[0_0_0_4px_rgba(17,24,39,0.10)] text-[14px] py-3"
          buttonStyle="padding: 10px 16px !important;"
        >
          Confirm now
        </Button>
      </div>
    </div>
  </div>
  <Transition
    enter-active-class="transition duration-300 ease-out"
    enter-from-class="transform -translate-y-4 opacity-0"
    enter-to-class="transform translate-y-0 opacity-100"
    leave-active-class="transition duration-200 ease-in"
    leave-from-class="transform translate-y-0 opacity-100"
    leave-to-class="transform -translate-y-4 opacity-0"
  >
    <div
      id="prebook-chat-component-overview-confirm"
      v-if="$payload.confirm"
      class="px-4 py-[15px]"
    >
      <div class="inline-flex w-[50px]">
        <img
          :src="$globalSetting?.settings?.business?.full_logo_url"
          :alt="$globalSetting?.settings?.business?.name"
          class="w-full object-contain"
        />
      </div>
      <h2 class="text-[20px]">
        Congratulations! 🎉 Your booking has been confirmed. Here are the
        details.
      </h2>
      <div>
        <div class="flex items-center gap-4 py-3">
          <div class="flex items-center gap-2">
            <Icon name="receiptCheck" class="text-[#039855]" size="30px" />
            <div class="text-[#101828] text-sm font-medium">
              Your Appointment is confirmed
            </div>
          </div>
        </div>

        <div class="py-5 border-y border-[#EAECF0]">
          <div class="flex items-center gap-3">
            <div
              class="w-[50px] h-[50px] flex items-center justify-center rounded-[6px] flex-shrink-0"
              :style="{
                'background-color': $payload?.service?.color,
              }"
            >
              <img
                v-if="$payload?.service?.thumbnail_image_url"
                :src="$payload?.service?.thumbnail_image_url"
                :alt="$payload?.service?.name"
                class="w-[50px] h-[50px] object-cover rounded-[6px] border-2 border-white"
              />
              <span
                v-else
                class="inline-flex text-[20px] text-white font-medium"
              >
                {{ $payload.service.name.substring(0, 1).toUpperCase() }}
              </span>
            </div>
            <div>
              <div class="text-[#101828] text-lg font-semibold">
                {{ $payload.service.name }}
              </div>
              <div class="text-[#475467] text-sm">
                {{ $payload.staff.name }}
              </div>
            </div>
            <div class="text-[#101828] text-lg font-semibold ml-auto">
              {{ getPrice($payload.service.price) }}
            </div>
          </div>
        </div>

        <div class="mt-5 mb-4 flex flex-col gap-5">
          <div
            class="flex items-center justify-between gap-3 text-[#101828] text-sm"
          >
            <div>Duration</div>
            <div>
              {{
                showDurationUnit(
                  $payload.service.duration,
                  $payload.service.duration_unit
                ) || "0 min"
              }}
            </div>
          </div>
          <div
            class="flex items-center justify-between gap-3 text-[#101828] text-sm"
          >
            <div>Time</div>
            <div>
              {{ moment($payload.service.time).format("DD MMM YYYY h:mm A") }}
            </div>
          </div>
          <!-- <div
            class="flex items-center justify-between gap-3 text-[#101828] text-sm"
          >
            <div>Location</div>
            <div class="max-w-[50%] text-end">
              6391 Elgin St. Celina, Delaware 10299
            </div>
          </div> -->
        </div>

        <div class="flex gap-5 py-3">
          <Button
            @click="finishBooking"
            size="sm"
            radius="square"
            class="w-full border-[2px] bg-gray-800 hover:bg-gray-900 border-gray-800 font-medium focus:shadow-[0_0_0_4px_rgba(17,24,39,0.10)] text-[14px] py-3"
            buttonStyle="padding: 10px 16px !important;"
          >
            I'm done
          </Button>
        </div>
      </div>
    </div>
  </Transition>
</template>
